import React, { createContext, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import type { UserMenuDataShapes } from '../../../common/types';
import { buildUserMenuDataShapes } from '../../../common/utils/build-user-menu-data-shapes';
import { useItemsAndShortcuts } from '../../items-and-shortcuts-provider';
import { useOptions } from '../../options-provider';

import type { UserMenuDataShapesProviderProps } from './types';

export const UserMenuDataShapesContext = createContext<UserMenuDataShapes | undefined>(undefined);

/**
 * Stores the repaired user menu data, in a variety of shapes, and makes it available to child components.
 * This component exists so that it is easy for child components to read and update the user menu data independently of the user side nav data.
 */
export function UserMenuDataShapesProvider({
	children,
	rawUserMenuData,
	setUserMenuData,
}: UserMenuDataShapesProviderProps) {
	const { disableMenuCustomization } = useOptions();
	const { allItems, rawShortcuts } = useItemsAndShortcuts();

	const userMenuDataShapes = useMemo(
		(): UserMenuDataShapes =>
			buildUserMenuDataShapes(
				Object.keys(allItems),
				Object.keys(rawShortcuts ?? {}),
				// All menu items are visible when there is no user menu data
				disableMenuCustomization ? [] : rawUserMenuData,
				setUserMenuData,
			),
		[allItems, disableMenuCustomization, rawUserMenuData, setUserMenuData, rawShortcuts],
	);

	return (
		<UserMenuDataShapesContext.Provider value={userMenuDataShapes}>
			{children}
		</UserMenuDataShapesContext.Provider>
	);
}

export function useUserMenuDataShapes() {
	const context = useContext(UserMenuDataShapesContext);

	invariant(
		context,
		'useUserMenuDataShapes must be used inside an initialized UserMenuDataShapesProvider',
	);

	return context;
}
